/**
 * WalletPage — /wallet
 *
 * Sections:
 *  1. Balance card (violet gradient, ZAR formatted)
 *  2. Deposit button → PayStack checkout, poll for confirm on return
 *  3. Withdrawal form (min R10)
 *  4. Transaction history with shimmer loading
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  getBalance,
  getTransactions,
  initDeposit,
  withdrawFunds,
  type Transaction,
} from '../services/api';
import { formatZAR } from '../utils/currency';

function txnIcon(txnType: Transaction['txn_type']): string {
  switch (txnType) {
    case 'deposit': return '↓';
    case 'withdrawal': return '↑';
    case 'bet_debit': return '🎯';
    case 'bet_win': return '🏆';
    case 'bet_refund': return '↩';
    case 'cashout': return '💰';
    default: return '•';
  }
}

function txnLabel(txnType: Transaction['txn_type']): string {
  switch (txnType) {
    case 'deposit': return 'Deposit';
    case 'withdrawal': return 'Withdrawal';
    case 'bet_debit': return 'Bet placed';
    case 'bet_win': return 'Winnings';
    case 'bet_refund': return 'Bet refund';
    case 'cashout': return 'Cashout';
    default: return txnType;
  }
}

function isCredit(txnType: Transaction['txn_type']): boolean {
  return ['deposit', 'bet_win', 'bet_refund', 'cashout'].includes(txnType);
}

// ── Shimmer placeholder ────────────────────────────────────────────────────────

function ShimmerRow() {
  return (
    <li className="flex items-center gap-3 py-3">
      <div className="shimmer h-8 w-8 rounded-full" />
      <div className="flex-1 space-y-1">
        <div className="shimmer h-3 w-24 rounded" />
        <div className="shimmer h-3 w-16 rounded" />
      </div>
      <div className="shimmer h-4 w-16 rounded" />
    </li>
  );
}

// ── Deposit amount modal ───────────────────────────────────────────────────────

interface DepositModalProps {
  onConfirm: (amountCents: number) => void;
  onCancel: () => void;
}

function DepositModal({ onConfirm, onCancel }: DepositModalProps) {
  const [rands, setRands] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseFloat(rands);
    if (!isNaN(amount) && amount >= 10) {
      onConfirm(Math.round(amount * 100));
    }
  };

  const amountNum = parseFloat(rands);
  const valid = !isNaN(amountNum) && amountNum >= 10;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm rounded-card bg-white p-6 shadow-modal">
        <h2 className="mb-4 font-heading text-xl font-bold text-text-primary">
          Deposit funds
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="deposit-amount" className="mb-1 block text-sm font-medium text-text-primary">
              Amount (ZAR, min R10)
            </label>
            <input
              ref={inputRef}
              id="deposit-amount"
              type="number"
              min="10"
              step="1"
              required
              value={rands}
              onChange={(e) => setRands(e.target.value)}
              className="w-full min-h-[44px] rounded-button border border-border bg-surface px-4 py-3 text-text-primary outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              placeholder="100"
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 min-h-[44px] rounded-button border border-border bg-white py-3 font-medium text-text-primary transition-colors hover:bg-surface-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!valid}
              className="flex-1 min-h-[44px] cursor-pointer rounded-button bg-primary py-3 font-bold text-white shadow-btn transition-colors hover:bg-primary-dark disabled:opacity-60"
            >
              Continue to pay
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function WalletPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [balanceCents, setBalanceCents] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [txLoading, setTxLoading] = useState(true);
  const [txError, setTxError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositError, setDepositError] = useState<string | null>(null);

  // Polling state for post-deposit confirmation
  const [confirming, setConfirming] = useState(false);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const [withdrawRands, setWithdrawRands] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState<string | null>(null);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  // ── Fetch balance ────────────────────────────────────────────────────────────

  const fetchBalance = async (): Promise<number> => {
    const res = await getBalance();
    const cents = res.data.balance_cents;
    setBalanceCents(cents);
    return cents;
  };

  // ── Fetch transactions ────────────────────────────────────────────────────────

  const fetchTransactions = async (p = page) => {
    setTxLoading(true);
    setTxError(null);
    try {
      const res = await getTransactions(p);
      setTransactions(res.data.items);
      setTotalPages(res.data.pages);
    } catch {
      setTxError('Failed to load transactions.');
    } finally {
      setTxLoading(false);
    }
  };

  // ── Initial load ─────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchBalance().catch(() => setBalanceCents(0));
    fetchTransactions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Deposit-success poll (prizes pattern) ────────────────────────────────────
  // When the user returns with ?deposit=success, show "Confirming..." and poll
  // until the balance increases from the pre-redirect snapshot.

  useEffect(() => {
    if (searchParams.get('deposit') !== 'success') return;
    // Remove the query param so a refresh doesn't re-trigger
    setSearchParams({}, { replace: true });
    setConfirming(true);

    const storedBalance = sessionStorage.getItem('pre_deposit_balance');
    // Use the stored snapshot if available. Fall back to -1 so that ANY balance
    // (including 0) is treated as an increase when sessionStorage was lost
    // (e.g. private browsing or tab suspended during PayStack redirect).
    // Using -1 means we wait for at least one successful balance response
    // rather than short-circuiting immediately on the null branch.
    const priorBalance = storedBalance !== null ? parseInt(storedBalance, 10) : -1;
    sessionStorage.removeItem('pre_deposit_balance');

    const startPolling = () => {
      pollTimer.current = setInterval(async () => {
        try {
          const currentBalance = await fetchBalance();
          if (currentBalance > priorBalance) {
            clearInterval(pollTimer.current!);
            pollTimer.current = null;
            setConfirming(false);
            fetchTransactions(1);
          }
        } catch {
          // ignore transient errors, keep polling
        }
      }, 3000);
    };

    // Stop polling after 60s max (avoid infinite loop if webhook never fires)
    startPolling();
    const timeout = setTimeout(() => {
      if (pollTimer.current) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
        setConfirming(false);
      }
    }, 60_000);

    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Deposit handler ───────────────────────────────────────────────────────────

  const handleDeposit = async (amountCents: number) => {
    setShowDepositModal(false);
    setDepositLoading(true);
    setDepositError(null);
    try {
      const successUrl = `${window.location.href.split('?')[0]}?deposit=success`;
      const cancelUrl = window.location.href.split('?')[0];
      // Snapshot current balance before leaving
      const currentBalance = balanceCents ?? 0;
      sessionStorage.setItem('pre_deposit_balance', String(currentBalance));
      const res = await initDeposit(amountCents, successUrl, cancelUrl);
      window.location.href = res.data.checkout_url;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setDepositError(
        status === 502
          ? 'Payment service unavailable. Please try again shortly.'
          : 'Failed to start deposit. Please try again.',
      );
    } finally {
      setDepositLoading(false);
    }
  };

  // ── Withdrawal handler ────────────────────────────────────────────────────────

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    setWithdrawError(null);
    setWithdrawSuccess(null);
    const amountCents = Math.round(parseFloat(withdrawRands) * 100);
    if (isNaN(amountCents) || amountCents < 1000) {
      setWithdrawError('Minimum withdrawal is R10.');
      return;
    }
    if (balanceCents !== null && amountCents > balanceCents) {
      setWithdrawError('Insufficient balance.');
      return;
    }
    setWithdrawLoading(true);
    try {
      await withdrawFunds(amountCents);
      setWithdrawRands('');
      setWithdrawSuccess('Withdrawal request submitted. Processed within 2-3 business days.');
      await fetchBalance();
      await fetchTransactions(1);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 400) {
        setWithdrawError(detail ?? 'Invalid withdrawal amount.');
      } else {
        setWithdrawError('Withdrawal failed. Please try again.');
      }
    } finally {
      setWithdrawLoading(false);
    }
  };

  // ── Pagination ────────────────────────────────────────────────────────────────

  const goToPage = (p: number) => {
    setPage(p);
    fetchTransactions(p);
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main className="flex-1 mx-auto w-full max-w-2xl px-4 py-8 sm:px-6">

        {/* ── Balance card ─────────────────────────────────────────────────── */}
        <section
          className="mb-6 rounded-card p-6 text-center shadow-card"
          style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 60%, #f97316 100%)' }}
        >
          <p className="mb-1 text-sm font-medium text-white/80">Your balance</p>
          {confirming ? (
            <>
              <div className="shimmer mx-auto mb-2 h-10 w-40 rounded-pill" />
              <p className="text-sm text-white/80">Confirming your deposit...</p>
            </>
          ) : balanceCents === null ? (
            <div className="shimmer mx-auto h-10 w-40 rounded-pill" />
          ) : (
            <p className="font-heading text-4xl font-bold text-white">
              {formatZAR(balanceCents)}
            </p>
          )}
          <p className="mt-1 text-xs text-white/60">ZAR — South African Rand</p>
        </section>

        {/* ── Action buttons ──────────────────────────────────────────────── */}
        <div className="mb-6 flex gap-3">
          <button
            onClick={() => {
              setDepositError(null);
              setShowDepositModal(true);
            }}
            disabled={depositLoading || confirming}
            className="flex-1 min-h-[44px] cursor-pointer rounded-button bg-primary py-3 font-bold text-white shadow-btn transition-colors hover:bg-primary-dark disabled:opacity-60"
          >
            {depositLoading ? 'Loading...' : 'Deposit'}
          </button>
        </div>

        {depositError && (
          <div className="mb-4 rounded-button bg-error/10 px-4 py-3 text-sm text-error" role="alert">
            {depositError}
          </div>
        )}

        {/* ── Withdrawal form ─────────────────────────────────────────────── */}
        <section className="mb-6 rounded-card border border-border bg-surface p-6 shadow-card">
          <h2 className="mb-4 font-heading text-lg font-bold text-text-primary">
            Withdraw funds
          </h2>
          <form onSubmit={handleWithdraw} className="space-y-3">
            <div>
              <label htmlFor="withdraw-amount" className="mb-1 block text-sm font-medium text-text-primary">
                Amount (ZAR, min R10)
              </label>
              <input
                id="withdraw-amount"
                type="number"
                min="10"
                step="1"
                required
                value={withdrawRands}
                onChange={(e) => setWithdrawRands(e.target.value)}
                className="w-full min-h-[44px] rounded-button border border-border bg-surface px-4 py-3 text-text-primary outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="Min R10"
              />
            </div>
            {withdrawError && (
              <p className="text-sm text-error" role="alert">{withdrawError}</p>
            )}
            {withdrawSuccess && (
              <p className="text-sm text-success" role="status">{withdrawSuccess}</p>
            )}
            <button
              type="submit"
              disabled={withdrawLoading}
              className="min-h-[44px] w-full cursor-pointer rounded-button border border-primary bg-white py-3 font-bold text-primary transition-colors hover:bg-primary/5 disabled:opacity-60"
            >
              {withdrawLoading ? 'Processing...' : 'Withdraw'}
            </button>
          </form>
        </section>

        {/* ── Transaction history ─────────────────────────────────────────── */}
        <section>
          <h2 className="mb-4 font-heading text-lg font-bold text-text-primary">
            Transactions
          </h2>
          {txError && (
            <p className="mb-3 rounded-button bg-error/10 px-4 py-3 text-sm text-error">
              {txError}
            </p>
          )}
          <div className="rounded-card border border-border bg-surface shadow-card">
            {txLoading ? (
              <ul className="divide-y divide-border px-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <ShimmerRow key={i} />
                ))}
              </ul>
            ) : transactions.length === 0 ? (
              <p className="px-6 py-10 text-center text-sm text-text-muted">
                No transactions yet.
              </p>
            ) : (
              <>
                <ul className="divide-y divide-border px-4">
                  {transactions.map((tx) => (
                    <li key={tx.id} className="flex items-center gap-3 py-3">
                      {/* Icon */}
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                          isCredit(tx.txn_type)
                            ? 'bg-success/10 text-success'
                            : 'bg-error/10 text-error'
                        }`}
                      >
                        {txnIcon(tx.txn_type)}
                      </span>

                      {/* Label + ref */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text-primary">
                          {txnLabel(tx.txn_type)}
                        </p>
                        {tx.reference && (
                          <p className="truncate text-xs text-text-muted">{tx.reference}</p>
                        )}
                        <p className="text-xs text-text-muted">
                          {new Date(tx.created_at).toLocaleDateString('en-ZA', {
                            year: 'numeric', month: 'short', day: 'numeric',
                          })}
                        </p>
                      </div>

                      {/* Amount */}
                      <span
                        className={`shrink-0 font-mono text-sm font-semibold tabular-nums ${
                          isCredit(tx.txn_type) ? 'text-success' : 'text-error'
                        }`}
                      >
                        {isCredit(tx.txn_type) ? '+' : ''}{formatZAR(Math.abs(tx.amount_cents))}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 border-t border-border px-4 py-3">
                    <button
                      onClick={() => goToPage(page - 1)}
                      disabled={page <= 1}
                      className="min-h-[36px] rounded-button border border-border px-3 py-1 text-sm text-text-primary disabled:opacity-40"
                    >
                      Prev
                    </button>
                    <span className="text-sm text-text-muted">
                      {page} / {totalPages}
                    </span>
                    <button
                      onClick={() => goToPage(page + 1)}
                      disabled={page >= totalPages}
                      className="min-h-[36px] rounded-button border border-border px-3 py-1 text-sm text-text-primary disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />

      {/* ── Deposit modal overlay ───────────────────────────────────────── */}
      {showDepositModal && (
        <DepositModal
          onConfirm={handleDeposit}
          onCancel={() => setShowDepositModal(false)}
        />
      )}
    </div>
  );
}
